import {
  WEEK_STATUS_OPTIONS,
  type CalendarDayFilters,
  type CalendarMonthFilters,
  type CalendarWeekFilters,
} from '@elva/ui';

/**
 * Options-drawer filters → query parameters for /api/calendar/{day,next7,month}.
 * Only non-default values are sent, so an unfiltered request is identical to the legacy one.
 * Unknown enum values make the backend answer 400, so every list is checked against what it accepts.
 */
export type CalendarQueryParams = Record<string, string>;

const BOOKING_VALUES = ['SCHEDULED', 'RESCHEDULED', 'CANCELLED', 'BROKEN', 'COMPLETED'] as const;
const CONFIRMATION_VALUES = ['UNCONFIRMED', 'ATTEMPTED', 'CONFIRMED'] as const;
const VISIT_VALUES = [
  'NOT_ARRIVED',
  'ARRIVED',
  'SEATED',
  'TREATMENT_DONE',
  'CHECKED_OUT',
  'NO_SHOW',
] as const;

const STATUS_AXES: readonly (readonly string[])[] = [
  BOOKING_VALUES,
  CONFIRMATION_VALUES,
  VISIT_VALUES,
];

const FLAG_VALUES: ReadonlySet<string> = new Set([
  'elig_fail',
  'med_alert',
  'lab_missing',
  'forms_inc',
  'preauth_p',
  'new_pt',
  'unconfirmed',
  'balance',
  'anxiety',
  'big_case',
  'elig_ok',
  'forms_ok',
  'reminded',
  'birthday',
  'vip',
]);

const QUICK_VALUES: ReadonlySet<string> = new Set([
  'red',
  'unconfirmed',
  'notready',
  'building',
  'newpt',
]);

const MAX_MIN_PRODUCTION = 5000;

/** The values still ticked on one axis, or undefined when nothing on it is hidden. */
function ticked(all: readonly string[], hidden: ReadonlySet<string>): string | undefined {
  if (!all.some((value) => hidden.has(value))) return undefined;
  const kept = all.filter((value) => !hidden.has(value));
  return kept.length > 0 ? kept.join(',') : undefined;
}

function csv(values: ReadonlySet<string>): string | undefined {
  return values.size > 0 ? [...values].sort().join(',') : undefined;
}

function compact(entries: Record<string, string | undefined>): CalendarQueryParams {
  const out: CalendarQueryParams = {};
  for (const [key, value] of Object.entries(entries)) {
    if (value) out[key] = value;
  }
  return out;
}

/** True when un-ticking has hidden every value of a day status axis (nothing would be left to show). */
export function hidesWholeDayStatusAxis(hidden: ReadonlySet<string>): boolean {
  return STATUS_AXES.some((axis) => axis.every((value) => hidden.has(value)));
}

/** True when the un-ticked week checkboxes leave nothing on an axis (Confirmed + Pending together). */
export function hidesWholeWeekStatusAxis(hidden: ReadonlySet<string>): boolean {
  const hiddenValues = weekHiddenValues(hidden);
  return [CONFIRMATION_VALUES, VISIT_VALUES].some((axis) =>
    axis.every((value) => hiddenValues.has(value)),
  );
}

function weekHiddenValues(hiddenIds: ReadonlySet<string>): Set<string> {
  return new Set(
    WEEK_STATUS_OPTIONS.filter((option) => hiddenIds.has(option.id)).flatMap((option) => [
      ...option.values,
    ]),
  );
}

export function dayFilterParams(filters: CalendarDayFilters): CalendarQueryParams {
  const flags = new Set([...filters.flags].filter((flag) => FLAG_VALUES.has(flag)));
  const minProduction = Math.min(Math.max(filters.minProduction, 0), MAX_MIN_PRODUCTION);

  return compact({
    quick: QUICK_VALUES.has(filters.quick) ? filters.quick : undefined,
    booking: ticked(BOOKING_VALUES, filters.hiddenStatuses),
    confirmation: ticked(CONFIRMATION_VALUES, filters.hiddenStatuses),
    visit: ticked(VISIT_VALUES, filters.hiddenStatuses),
    provider: csv(filters.providers),
    operatory: csv(filters.operatories),
    flags: csv(flags),
    min_production_cents: minProduction > 0 ? String(minProduction * 100) : undefined,
  });
}

export function weekFilterParams(filters: CalendarWeekFilters): CalendarQueryParams {
  const hiddenValues = weekHiddenValues(filters.hiddenStatuses);

  return compact({
    range_days: filters.rangeDays === 14 ? '14' : undefined,
    readiness: filters.readiness === 'yr' || filters.readiness === 'red' ? filters.readiness : undefined,
    confirmation: ticked(CONFIRMATION_VALUES, hiddenValues),
    visit: ticked(VISIT_VALUES, hiddenValues),
  });
}

export function monthFilterParams(filters: CalendarMonthFilters): CalendarQueryParams {
  return compact({
    highlight:
      filters.highlight === 'under' || filters.highlight === 'issues'
        ? filters.highlight
        : undefined,
  });
}

/** Stable string for React Query keys — list order and key order do not matter. */
export function filterParamsKey(params: CalendarQueryParams): string {
  return Object.entries(params)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
}
